/**
 * Unplaced people — registrations the matcher could not put on a retailer:
 * an unmatched Salesforce account, several candidate retailers, or nothing at
 * all. Shown in the Unassigned view of the Retailers workspace. Pick a retailer
 * (candidates first) and Approve, or decline / remove the row. Admin-only.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Check, X, Trash2, Mail, AlertTriangle, RefreshCw, Building2, ExternalLink } from 'lucide-react';
import { ConfirmModal } from '../../../components/admin/ConfirmModal';
import { fetchRetailerMembers, memberAction, deleteMember, reclassifyMember, type RetailerMemberRow } from '../../../services/adminRetailerMembersApi';
import type { RetailerTreeNode } from '../../../services/adminRetailerTreeApi';

const SOURCE_LABEL: Record<string, string> = {
  salesforce: 'Salesforce contact', salesforce_parent: 'Salesforce (parent account)', domain: 'email domain', invite: 'invite link', self: 'join page', admin: 'added by admin',
};
const fmtDate = (iso: string | null) => (iso ? iso.replace('T', ' ').slice(0, 16) : '—');

export const UnplacedPeople: React.FC<{ nodes: RetailerTreeNode[]; canEdit: boolean; onChanged: () => void }> = ({ nodes, canEdit, onChanged }) => {
  const [rows, setRows] = useState<RetailerMemberRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState<Map<number, number>>(new Map());
  const [confirm, setConfirm] = useState<{ kind: 'remove' | 'reject'; row: RetailerMemberRow } | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await fetchRetailerMembers('open');
      setRows(d.members.filter(m => m.retailerId === null));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const options = useMemo(() => {
    const roots = nodes.filter(n => n.id !== 1 && n.parentRetailerId === null).sort((a, b) => a.name.localeCompare(b.name));
    const out: { id: number; label: string }[] = [];
    for (const r of roots) {
      out.push({ id: r.id, label: r.name });
      nodes.filter(c => c.parentRetailerId === r.id).sort((a, b) => a.name.localeCompare(b.name)).forEach(c => out.push({ id: c.id, label: `  ↳ ${c.name}` }));
    }
    return out;
  }, [nodes]);

  const run = async (fn: () => Promise<string | void>) => {
    setBusy(true); setError(null); setMessage(null);
    try { const msg = await fn(); if (msg) setMessage(msg); await load(); onChanged(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Action failed'); }
    finally { setBusy(false); }
  };

  const chosen = (m: RetailerMemberRow) => pick.get(m.id) ?? (m.candidates.length === 1 ? m.candidates[0].id : undefined);

  if (!loading && rows.length === 0) return <div className="text-muted small py-3">Nobody is waiting without a retailer. People the matcher can place show up under their retailer's People tab.</div>;

  return (
    <div>
      <p className="text-muted small mb-2">Registrations the matcher could not place: their Salesforce account is not mapped to any retailer, or several retailers matched. Pick the retailer and approve; they get access to the entire retailer, which you can narrow afterwards.</p>
      {message && <div className="alert alert-success py-2 small">{message}</div>}
      {error && <div className="alert alert-danger py-2 small d-flex align-items-center gap-2"><AlertTriangle size={14} /> {error}</div>}
      <div className="table-responsive bg-white rounded-3 border">
        <table className="table table-sm table-hover align-middle mb-0 small">
          <thead className="table-light"><tr><th>Person</th><th>Why it could not be placed</th><th style={{ width: 300 }}>Retailer</th><th className="text-end">Actions</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={4} className="text-center text-muted py-3"><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</td></tr>
              : rows.map(m => (
                <tr key={m.id} className="table-warning">
                  <td>
                    <div className="fw-semibold">{[m.firstName, m.lastName].filter(Boolean).join(' ') || <span className="text-muted">(no name yet)</span>}</div>
                    <div className="text-muted d-flex align-items-center gap-1"><Mail size={11} /> {m.email}</div>
                    <div className="text-muted" style={{ fontSize: '0.85em' }}>via {SOURCE_LABEL[m.source] ?? m.source} · registered {fmtDate(m.createdAt)}</div>
                  </td>
                  <td>
                    {m.sfAccountName && <div>Salesforce account <strong>“{m.sfAccountName}”</strong> is not mapped to any retailer.{canEdit && <> <Link to={`/admin/retailers?new=1&name=${encodeURIComponent(m.sfAccountName)}`} className="d-inline-flex align-items-center gap-1"><Building2 size={12} /> Create it <ExternalLink size={10} /></Link></>}</div>}
                    {!m.sfAccountName && m.sfAccountId && <div>Salesforce account <code>{m.sfAccountId}</code> is not mapped to any retailer.</div>}
                    {m.candidates.length > 0 && <div>Matched several: {m.candidates.map(c => c.name).join(', ')}.</div>}
                    {m.matchedDomain && <div className="text-muted">@{m.matchedDomain}</div>}
                    {!m.sfAccountName && !m.sfAccountId && m.candidates.length === 0 && <span className="text-muted">No Salesforce, domain or invite match.</span>}
                  </td>
                  <td>
                    <select className="form-select form-select-sm" value={chosen(m) ?? ''} disabled={!canEdit || busy} onChange={e => setPick(prev => { const n = new Map(prev); n.set(m.id, Number(e.target.value)); return n; })}>
                      <option value="">— pick a retailer —</option>
                      {m.candidates.length > 0 && <optgroup label="Matched">{m.candidates.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</optgroup>}
                      <optgroup label="All retailers">{options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}</optgroup>
                    </select>
                    {m.sfAccountId && !m.sfAccountMapped && <div className="form-text mb-0">Approving also remembers this Salesforce account for the retailer.</div>}
                  </td>
                  <td className="text-end">
                    {canEdit ? (
                      <div className="d-inline-flex flex-wrap gap-1 justify-content-end">
                        <button type="button" className="btn btn-success btn-sm d-inline-flex align-items-center gap-1" disabled={busy || !chosen(m)} onClick={() => void run(async () => { await memberAction({ action: 'approve', id: m.id, retailerId: chosen(m), rememberSfAccount: !!m.sfAccountId && !m.sfAccountMapped }); return `${m.email} approved.`; })}><Check size={13} /> Approve</button>
                        <button type="button" className="btn btn-outline-danger btn-sm" disabled={busy} title="Decline" onClick={() => setConfirm({ kind: 'reject', row: m })}><X size={13} /></button>
                        <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} title="Re-run the matching" onClick={() => void run(async () => { await reclassifyMember(m.id); return 'Matching re-run.'; })}><RefreshCw size={13} /></button>
                        <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} title="Remove this row" onClick={() => setConfirm({ kind: 'remove', row: m })}><Trash2 size={13} /></button>
                      </div>
                    ) : <span className="text-muted">Read-only</span>}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <ConfirmModal open={!!confirm} title={confirm?.kind === 'reject' ? `Decline ${confirm.row.email}?` : `Remove ${confirm?.row.email}?`} confirmLabel={confirm?.kind === 'reject' ? 'Decline' : 'Remove'} variant="danger" busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => { const c = confirm; setConfirm(null); if (!c) return; void run(async () => { if (c.kind === 'reject') await memberAction({ action: 'reject', id: c.row.id }); else await deleteMember(c.row.id); return c.kind === 'reject' ? 'Declined.' : 'Removed. They will be re-matched on their next sign-in.'; }); }}>
        <p className="mb-0">{confirm?.kind === 'reject' ? 'The row is kept as declined so they are not proposed again.' : 'The row goes; they will be re-matched the next time they sign in.'}</p>
      </ConfirmModal>
    </div>
  );
};

export default UnplacedPeople;
